import { ApiError, parseValidationError } from './api';

export type ApiErrorFieldStrategy<TField extends string> = Partial<Record<string, TField>>;

export function createApiErrorFieldStrategy<TField extends string>(
  strategy: ApiErrorFieldStrategy<TField>
): ApiErrorFieldStrategy<TField> {
  return strategy;
}

export function clearValidationField<T extends { form?: string }>(
  prev: T,
  field: keyof T
): T {
  return {
    ...prev,
    [field]: undefined,
    form: undefined,
  };
}

export function handleValidationError<TField extends string>(
  error: unknown,
  fields: readonly TField[],
  applyParsedValidation: (parsed: { fieldErrors: Partial<Record<TField, string>>; message: string }) => void,
  onUnparsed: (error: unknown) => void
): void {
  const parsedValidation = parseValidationError(error, fields);
  if (parsedValidation) {
    applyParsedValidation(parsedValidation);
    return;
  }

  onUnparsed(error);
}

export function messageFromUnknown(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function setFormErrorFromUnknown(
  error: unknown,
  setFormError: (message: string) => void,
  fallback: string
): void {
  setFormError(messageFromUnknown(error, fallback));
}

export function handleValidationErrorWithFormFallback<TField extends string>(
  error: unknown,
  fields: readonly TField[],
  applyParsedValidation: (parsed: { fieldErrors: Partial<Record<TField, string>>; message: string }) => void,
  setFormError: (message: string) => void,
  fallback: string
): void {
  handleValidationError(
    error,
    fields,
    applyParsedValidation,
    (unparsedError) => setFormErrorFromUnknown(unparsedError, setFormError, fallback)
  );
}

export function applyApiErrorFieldStrategy<TField extends string>(
  error: unknown,
  strategy: ApiErrorFieldStrategy<TField>,
  setFieldErrors: (errors: Partial<Record<TField, string>>) => void
): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const field = strategy[error.code];
  if (!field) {
    return false;
  }

  const fieldError = { [field]: error.message } as Partial<Record<TField, string>>;
  setFieldErrors(fieldError);
  return true;
}

export function handleValidationErrorWithApiFieldStrategy<TField extends string>(
  error: unknown,
  fields: readonly TField[],
  strategy: ApiErrorFieldStrategy<TField>,
  applyParsedValidation: (parsed: { fieldErrors: Partial<Record<TField, string>>; message: string }) => void,
  setFieldErrors: (errors: Partial<Record<TField, string>>) => void,
  setFormError: (message: string) => void,
  fallback: string
): void {
  if (applyApiErrorFieldStrategy(error, strategy, setFieldErrors)) {
    return;
  }

  handleValidationErrorWithFormFallback(error, fields, applyParsedValidation, setFormError, fallback);
}
